class UgcVideoGallery extends HTMLElement {
  connectedCallback() {
    const alreadyBound = this._bound;
    this._bound = true;
    this._place(document.readyState !== "loading");

    if (alreadyBound) return;

    this.autoplay = this.dataset.autoplay !== "false";
    this.modalRatioMode = this.dataset.modalRatio || "follow";
    this.modal = this.querySelector("[data-ugc-modal]");
    this.slides = Array.from(this.querySelectorAll("[data-ugc-slide]"));
    this.openButtons = Array.from(this.querySelectorAll("[data-ugc-open]"));
    this.closeButton = this.querySelector("[data-ugc-close]");
    this.prevButton = this.querySelector("[data-ugc-prev]");
    this.nextButton = this.querySelector("[data-ugc-next]");
    this.soundButton = this.querySelector("[data-ugc-sound]");
    this.soundOn = this.soundButton?.querySelector(".product-info__ugc-video-sound-on");
    this.soundOff = this.soundButton?.querySelector(".product-info__ugc-video-sound-off");
    this.scroller = this.querySelector("[data-ugc-scroller]");
    this.activeIndex = 0;
    this._pointerDelta = 0;

    if (document.readyState === "loading") {
      this._onReady = () => this._place(true);
      document.addEventListener("DOMContentLoaded", this._onReady, { once: true });
    }

    this._onSectionLoad = () => this._place(true);
    document.addEventListener("shopify:section:load", this._onSectionLoad);

    if (!this.modal || !this.slides.length || !this.openButtons.length) return;

    this._onOpen = this._onOpen.bind(this);
    this._requestClose = this._requestClose.bind(this);
    this._cleanupModal = this._cleanupModal.bind(this);
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._onSound = this._onSound.bind(this);
    this._onBackdrop = this._onBackdrop.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._updateScrollable = this._updateScrollable.bind(this);

    this.openButtons.forEach((button) => button.addEventListener("click", this._onOpen));
    this.closeButton?.addEventListener("click", this._requestClose);
    this.prevButton?.addEventListener("click", this._onPrev);
    this.nextButton?.addEventListener("click", this._onNext);
    this.soundButton?.addEventListener("click", this._onSound);
    this.modal.addEventListener("click", this._onBackdrop);
    this.modal.addEventListener("close", this._cleanupModal);
    document.addEventListener("keydown", this._onKeydown);

    if (this.scroller) {
      this.scroller.addEventListener("pointerdown", this._onPointerDown);
      this.scroller.addEventListener("pointermove", this._onPointerMove);
      this.scroller.addEventListener("pointerup", this._onPointerUp);
      this.scroller.addEventListener("pointercancel", this._onPointerUp);
      this.scroller.addEventListener("scroll", this._updateScrollable, { passive: true });
      window.addEventListener("resize", this._updateScrollable);
      if (typeof ResizeObserver !== "undefined") {
        this._resizeObserver = new ResizeObserver(this._updateScrollable);
        this._resizeObserver.observe(this.scroller);
      }
    }

    if (this.autoplay) {
      this._bindCardYouTube();
      this._setCardVideos(true);
    } else {
      this._setCardVideos(false);
    }
    requestAnimationFrame(() => this._updateScrollable());
  }

  disconnectedCallback() {
    queueMicrotask(() => {
      if (this.isConnected) return;
      this._teardown();
    });
  }

  _teardown() {
    this._bound = false;
    this.openButtons?.forEach((button) => button.removeEventListener("click", this._onOpen));
    this.closeButton?.removeEventListener("click", this._requestClose);
    this.prevButton?.removeEventListener("click", this._onPrev);
    this.nextButton?.removeEventListener("click", this._onNext);
    this.soundButton?.removeEventListener("click", this._onSound);
    this.modal?.removeEventListener("click", this._onBackdrop);
    this.modal?.removeEventListener("close", this._cleanupModal);
    document.removeEventListener("keydown", this._onKeydown);
    document.removeEventListener("shopify:section:load", this._onSectionLoad);
    if (this._onReady) document.removeEventListener("DOMContentLoaded", this._onReady);
    if (this.scroller) {
      this.scroller.removeEventListener("pointerdown", this._onPointerDown);
      this.scroller.removeEventListener("pointermove", this._onPointerMove);
      this.scroller.removeEventListener("pointerup", this._onPointerUp);
      this.scroller.removeEventListener("pointercancel", this._onPointerUp);
      this.scroller.removeEventListener("scroll", this._updateScrollable);
    }
    window.removeEventListener("resize", this._updateScrollable);
    this._resizeObserver?.disconnect();
    document.body.classList.remove("ugc-video-modal-open");
  }

  _findSlot() {
    const slots = Array.from(document.querySelectorAll("[data-ugc-video-slot]"));
    if (slots.length <= 1) return slots[0] || null;

    const origin = document.getElementById(`shopify-section-${this.dataset.sectionId}`) || this;
    let best = null;
    let bestScore = Infinity;

    slots.forEach((slot) => {
      const before = origin.compareDocumentPosition(slot) & Node.DOCUMENT_POSITION_PRECEDING;
      const score = Math.abs(slot.getBoundingClientRect().top - origin.getBoundingClientRect().top) + (before ? 0 : 10000);
      if (score < bestScore) {
        best = slot;
        bestScore = score;
      }
    });

    return best;
  }

  _place(finalPass) {
    if (this.dataset.placement !== "product_info") {
      this.setAttribute("data-ugc-placed", "inline");
      return;
    }

    const currentSlot = this.closest("[data-ugc-video-slot]");
    if (currentSlot?.isConnected) {
      this.setAttribute("data-ugc-placed", "slot");
      return;
    }

    const slot = this._findSlot();
    if (slot) {
      slot.appendChild(this);
      this.setAttribute("data-ugc-placed", "slot");
      requestAnimationFrame(() => this._updateScrollable?.());
      return;
    }

    if (finalPass) this.setAttribute("data-ugc-placed", "inline");
  }

  _postYouTube(iframe, func, args = []) {
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func, args }), "*");
  }

  _bindCardYouTube() {
    this.querySelectorAll(".product-info__ugc-video-card iframe[data-ugc-card-yt]").forEach((iframe) => {
      if (iframe.dataset.ugcBound === "true") return;
      iframe.dataset.ugcBound = "true";
      const wrap = iframe.closest(".product-info__ugc-video-yt");
      const kick = () => {
        wrap?.classList.add("is-playing");
        this._postYouTube(iframe, "mute");
        this._postYouTube(iframe, "playVideo");
      };
      iframe.addEventListener("load", kick);
      if (iframe.getAttribute("src")) {
        window.setTimeout(kick, 400);
        window.setTimeout(kick, 1200);
      }
    });
  }

  _setCardVideos(shouldPlay) {
    if (!this.autoplay && shouldPlay) return;

    this.querySelectorAll(".product-info__ugc-video-card video").forEach((video) => {
      video.muted = true;
      if (!shouldPlay) {
        video.pause();
        return;
      }
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") playPromise.catch(() => {});
    });

    this.querySelectorAll(".product-info__ugc-video-card iframe[data-ugc-card-yt]").forEach((iframe) => {
      const wrap = iframe.closest(".product-info__ugc-video-yt");
      const src = iframe.dataset.src || iframe.getAttribute("src") || "";
      if (!src) return;

      if (shouldPlay) {
        if (!iframe.getAttribute("src")) iframe.src = src;
        wrap?.classList.add("is-playing");
        this._postYouTube(iframe, "mute");
        this._postYouTube(iframe, "playVideo");
      } else {
        this._postYouTube(iframe, "pauseVideo");
      }
    });
  }

  _applyModalRatio(slide) {
    if (this.modalRatioMode !== "follow" || !slide) return;
    const ratio = slide.getAttribute("data-aspect-ratio") || slide.style.getPropertyValue("--ugc-slide-ratio") || "9 / 16";
    this.style.setProperty("--ugc-modal-ratio", ratio);
    slide.style.setProperty("--ugc-slide-ratio", ratio);
  }

  _updateScrollable() {
    const el = this.scroller;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const canScroll = max > 2;
    const atEnd = el.scrollLeft >= max - 2;
    el.classList.toggle("is-scrollable", canScroll && !atEnd);
  }

  _onPointerDown(event) {
    if (event.target.closest("[data-ugc-open]")) return;
    this._pointerDelta = 0;
    this._pointerStartX = event.clientX;
    this._scrollStart = this.scroller.scrollLeft;
    this._dragScroll = event.pointerType === "mouse";
    if (this._dragScroll) this.scroller.setPointerCapture(event.pointerId);
  }

  _onPointerMove(event) {
    if (this._pointerStartX == null) return;
    const dx = event.clientX - this._pointerStartX;
    this._pointerDelta = Math.max(this._pointerDelta, Math.abs(dx));
    if (!this._dragScroll || this._pointerDelta <= 4) return;
    this.scroller.classList.add("is-dragging");
    this.scroller.scrollLeft = this._scrollStart - dx;
  }

  _onPointerUp() {
    this.scroller?.classList.remove("is-dragging");
    this._dragScroll = false;
    this._pointerStartX = null;
  }

  _setSoundMuted(muted) {
    if (!this.soundButton) return;
    this.soundButton.classList.toggle("is-muted", muted);
    if (this.soundOn) this.soundOn.hidden = muted;
    if (this.soundOff) this.soundOff.hidden = !muted;
  }

  _activateEmbed(slide) {
    const iframe = slide.querySelector("[data-ugc-embed]");
    if (!iframe) return;
    if (!iframe.getAttribute("src")) {
      iframe.src = iframe.dataset.src;
      return;
    }
    if (iframe.dataset.ugcEmbed === "youtube") {
      this._postYouTube(iframe, "playVideo");
    }
  }

  _deactivateEmbed(slide) {
    const iframe = slide.querySelector("[data-ugc-embed]");
    if (!iframe?.getAttribute("src")) return;
    if (iframe.dataset.ugcEmbed === "youtube") {
      this._postYouTube(iframe, "pauseVideo");
      return;
    }
    iframe.removeAttribute("src");
  }

  _setActive(index) {
    this.activeIndex = (index + this.slides.length) % this.slides.length;
    const prevIndex = (this.activeIndex - 1 + this.slides.length) % this.slides.length;
    const nextIndex = (this.activeIndex + 1) % this.slides.length;

    this.slides.forEach((slide, i) => {
      const video = slide.querySelector("video");
      slide.classList.remove("is-active", "is-prev", "is-next");

      if (i === this.activeIndex) {
        slide.classList.add("is-active");
        this._applyModalRatio(slide);
        if (video) {
          video.currentTime = 0;
          video.muted = true;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === "function") playPromise.catch(() => {});
        } else {
          this._activateEmbed(slide);
        }
      } else {
        if (i === prevIndex && this.slides.length > 1) slide.classList.add("is-prev");
        else if (i === nextIndex && this.slides.length > 1) slide.classList.add("is-next");
        video?.pause();
        this._deactivateEmbed(slide);
      }
    });

    const activeVideo = this.slides[this.activeIndex]?.querySelector("video");
    if (this.soundButton) this.soundButton.hidden = !activeVideo;
    this._setSoundMuted(true);
  }

  _openModal(index) {
    if (typeof this.modal.showModal === "function" && !this.modal.open) {
      this.modal.showModal();
    } else {
      this.modal.setAttribute("open", "");
    }
    document.body.classList.add("ugc-video-modal-open");
    this._setCardVideos(false);
    this._setActive(index);
    this.closeButton?.focus();
  }

  _onOpen(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this._pointerDelta > 8) {
      this._pointerDelta = 0;
      return;
    }
    this._opener = event.currentTarget;
    this._openModal(parseInt(event.currentTarget.getAttribute("data-index"), 10) || 0);
  }

  _requestClose() {
    if (this.modal.open && typeof this.modal.close === "function") {
      this.modal.close();
      return;
    }
    this._cleanupModal();
  }

  _cleanupModal() {
    this.modal.removeAttribute("open");
    document.body.classList.remove("ugc-video-modal-open");
    this.slides.forEach((slide) => {
      slide.querySelector("video")?.pause();
      this._deactivateEmbed(slide);
    });
    this._setCardVideos(true);
    this._opener?.focus();
  }

  _onPrev() {
    this._setActive(this.activeIndex - 1);
  }

  _onNext() {
    this._setActive(this.activeIndex + 1);
  }

  _onSound() {
    const activeVideo = this.slides[this.activeIndex]?.querySelector("video");
    if (!activeVideo) return;
    activeVideo.muted = !activeVideo.muted;
    this._setSoundMuted(activeVideo.muted);
  }

  _onBackdrop(event) {
    if (event.target === this.modal) this._requestClose();
  }

  _onKeydown(event) {
    if (!this.modal?.open && !this.modal?.hasAttribute("open")) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this._requestClose();
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this._setActive(this.activeIndex - 1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      this._setActive(this.activeIndex + 1);
    }
  }
}

if (!window.customElements.get("ugc-video-gallery")) {
  window.customElements.define("ugc-video-gallery", UgcVideoGallery);
}

if (window.Shopify?.designMode && !window.__ugcRecoverBound) {
  window.__ugcRecoverBound = true;

  const recoverUgcSections = () => {
    document.querySelectorAll(".shopify-section--ugc-videos").forEach((origin) => {
      const sectionId = origin.id.replace(/^shopify-section-/, "");
      if (!sectionId || origin.dataset.ugcRecovering === "true") return;
      if (origin.querySelector(".ugc-video-placeholder")) return;
      if (document.querySelector(`ugc-video-gallery[data-section-id="${sectionId}"]`)) return;

      origin.dataset.ugcRecovering = "true";
      const url = new URL(window.location.href);
      url.searchParams.set("section_id", sectionId);
      fetch(url.toString())
        .then((response) => response.text())
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, "text/html").getElementById(origin.id);
          if (fresh && origin.isConnected) origin.replaceWith(fresh);
        })
        .catch(() => {
          origin.dataset.ugcRecovering = "false";
        });
    });
  };

  document.addEventListener("shopify:section:load", recoverUgcSections);
}
